/**
 * calendar.ts — 내 업무 캘린더 묶음(2026-10-29 2차, 설계 docs/design/11_Google캘린더_연결설계.md '2차').
 *
 *   내 캘린더   users/{uid}/calendars/{autoId}  이름·색 키·순서 (본인만 읽기·쓰기)
 *               + 기본 캘린더 "default"(문서 없음, 항상 있음, 삭제·이름 변경 불가) — calendar_id 없는 개인 일정이 여기 속한다
 *   연동 캘린더 Google(users/{uid}/google_link/state 의 imported 목록, 서버 정본) · 네이버(준비 중)
 *   표시 설정   users/{uid}/calendar_prefs/state { hidden: string[] } — 끈 항목 키만 저장(기본은 전부 켬)
 *
 * 색은 hex 를 저장하지 않는다. 키 "1"~"10" → CSS 토큰 --chart-N (DESIGN.md 차트 팔레트). Google 캘린더 색은 Google 값 그대로.
 * 순수 타입·계산만. Firebase import 금지(domain 규칙).
 */

export const DEFAULT_CALENDAR_ID = "default";
export const DEFAULT_CALENDAR_NAME = "내 일정";
export const CALENDAR_NAME_MAX = 30;
/** 사용자가 만들 수 있는 캘린더 수(기본 제외) */
export const USER_CALENDARS_MAX = 10;

/** 색 키 — 순서대로 --chart-1 … --chart-10. 기본 캘린더는 중립(slate) */
export const CALENDAR_COLOR_KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10"] as const;
export type CalendarColorKey = (typeof CALENDAR_COLOR_KEYS)[number];
export function isCalendarColorKey(v: unknown): v is CalendarColorKey {
  return typeof v === "string" && (CALENDAR_COLOR_KEYS as readonly string[]).includes(v);
}
/** CSS 색 값(토큰) — 인라인 style 에 그대로 쓴다 */
export function calendarColorCss(key: CalendarColorKey | undefined): string {
  return key ? `var(--chart-${key})` : "var(--c-slate-400)";
}

export interface UserCalendar {
  id: string;
  name: string;
  color: CalendarColorKey;
  order: number;
  /** 기본 캘린더(문서 없음) */
  builtin?: boolean;
}

export const DEFAULT_CALENDAR: UserCalendar = {
  id: DEFAULT_CALENDAR_ID, name: DEFAULT_CALENDAR_NAME, color: "9", order: -1, builtin: true,
};

const str = (v: unknown) => (typeof v === "string" ? v : "");

/** 저장 문서 → UserCalendar. 이름이 없거나 색이 이상하면 null */
export function coerceUserCalendar(id: string, d: Record<string, unknown>): UserCalendar | null {
  const name = str(d.name).trim();
  if (!name || id === DEFAULT_CALENDAR_ID) return null;
  return {
    id,
    name: name.slice(0, CALENDAR_NAME_MAX),
    color: isCalendarColorKey(d.color) ? d.color : "1",
    order: typeof d.order === "number" && Number.isFinite(d.order) ? d.order : 0,
  };
}

/** 기본 먼저 → order → 이름 */
export function sortCalendars(list: UserCalendar[]): UserCalendar[] {
  return [...list].sort((a, b) => {
    if (!!a.builtin !== !!b.builtin) return a.builtin ? -1 : 1;
    if (a.order !== b.order) return a.order - b.order;
    return a.name.localeCompare(b.name, "ko");
  });
}

/** 입력 검증 — 실패하면 사용자에게 보여줄 문장 */
export function normalizeCalendarName(raw: string, existing: UserCalendar[], selfId?: string): { ok: true; name: string } | { ok: false; error: string } {
  const name = raw.trim();
  if (!name) return { ok: false, error: "캘린더 이름을 입력하세요." };
  if (name.length > CALENDAR_NAME_MAX) return { ok: false, error: `이름은 ${CALENDAR_NAME_MAX}자까지 입력할 수 있습니다.` };
  if (name === DEFAULT_CALENDAR_NAME && selfId !== DEFAULT_CALENDAR_ID) return { ok: false, error: `'${DEFAULT_CALENDAR_NAME}'은 기본 캘린더 이름입니다.` };
  if (existing.some((c) => c.id !== selfId && c.name === name)) return { ok: false, error: "같은 이름의 캘린더가 있습니다." };
  return { ok: true, name };
}

/** 아직 안 쓴 색 중 첫 번째(다 썼으면 순환) */
export function nextCalendarColor(existing: UserCalendar[]): CalendarColorKey {
  const used = new Set(existing.map((c) => c.color));
  return CALENDAR_COLOR_KEYS.find((k) => !used.has(k)) ?? CALENDAR_COLOR_KEYS[existing.length % CALENDAR_COLOR_KEYS.length];
}

// ───────────────────────── 저장 위치 키 (일정 모달 Select 값) ─────────────────────────

export const storeKeyErp = (calId: string) => `erp:${calId}`;
export const storeKeyGoogle = (calId: string) => `google:${calId}`;

// ───────────────────────── 표시 켜기/끄기 키 ─────────────────────────

/** 표시 설정 키 — 내 캘린더 = 그 id, 할 일 = "todo", Google = "google:<calId>", 법인 생산 = "prod:<법인>" */
export const VIS_TODO = "todo";
export const visKeyCalendar = (calId: string) => calId;
export const visKeyGoogle = (calId: string) => `google:${calId}`;
export const visKeyProd = (tenant: "joongang" | "gyeongwon") => `prod:${tenant}`;

export function coerceHidden(d: Record<string, unknown> | undefined): Set<string> {
  const arr = Array.isArray(d?.hidden) ? d!.hidden : [];
  return new Set(arr.filter((x): x is string => typeof x === "string"));
}
